import React, { useEffect, useState, useRef, useMemo } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  deleteExpense,
  getAllExpenses,
  getLedgerDetails,
  getPartyDetails,
} from "../../services/services";
import Loading from "../../components/loading";
import AddExpense from "./AddExpenses";
import moment from "moment";
import { toast } from "react-toastify";
import { confirmDelete } from "../../components/confirmDelete";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../components/Pagination";
import DataTable from "react-data-table-component";
import PartyCardDetail from "../party/partyCardDetail";
import SearchAndFilter from "../items/SearchAndFilter";

function Expenses() {
  const [expenses, setExpense] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModel, setShowModel] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const fetchExpenses = async (page = currentPage, pageLimit = limit) => {
    try {
      setLoading(true);
      setExpense([]);

      const payload = {
        page,
        limit: pageLimit,
        name: searchTerm,
      };

      if (dateRange.from) payload.dateFrom = dateRange.from;
      if (dateRange.to) payload.dateTo = dateRange.to;
      console.log("PAYLOAD =>", payload);
      const res = await getAllExpenses(payload);

      if (res.data.status) {
        setExpense(res.data.response.expenses || []);
        setCurrentPage(res.data.response.page || 1);
        setLimit(pageLimit);
        setTotalPages(res.data.response.totalPages || 1);
        setTotalItems(res.data.response.total);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchExpenses(page, limit);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
    fetchExpenses(1, newLimit);
  };

  const handleDelete = async (row) => {
    const name = row.title || "Expense";

    const isConfirm = await confirmDelete(`Expense - ${name} `);
    if (!isConfirm) return;

    setLoading(true);
    try {
      const res = await deleteExpense(row._id);
      if (res.data.status) {
        toast.success(res.data.message);
        fetchExpenses();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExpense = async (expense) => {
    try {
      // const partyId = sale.customerId?._id;
      const payload = { referenceId: expense._id };
      const res = await getLedgerDetails(payload);
      if (res.data.status) {
        setSelectedExpense(res.data.ledger?.[0]);
      }
    } catch (error) {
      console.error("Failed to fetch party balance", error);
      toast.error("Failed to load details");
    }
  };

  useEffect(() => {
    fetchExpenses(1, limit);
  }, [searchTerm, dateRange]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateRange]);
  /* ---------------- DATATABLE COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        name: "S.No",
        width: "80px",
        cell: (_, index) => (currentPage - 1) * limit + index + 1,
      },
      {
        name: "Expense Type",
        selector: (row) => row.title || "-",
        sortable: true,
        width: "140px",
      },
      {
        name: "Name",
        selector: (row) => row.paidTo || "-",
        sortable: true,
      },
      {
        name: "Payment Method",
        selector: (row) => row.paymentMethod || "-",
        width: "150px",
      },
      {
        name: "Amount (₹)",
        selector: (row) => `${row?.amount?.toLocaleString() || "0"}`,
        width: "150px",
      },
      {
        name: "Category",
        selector: (row) => row?.category || "-",
      },
      {
        name: "Date",
        selector: (row) => moment(row?.date).format("DD MMM, YYYY"),
      },
      {
        name: "Status",
        // center: true,
        cell: (row) => (
          <span
            className={`badge rounded-pill ${
              row.status === "paid"
                ? "bg-success"
                : row.status === "pending"
                  ? "bg-warning text-dark"
                  : row.status === "overdue"
                    ? "bg-danger"
                    : "bg-secondary"
            }`}
            style={{ fontSize: "11px" }}
          >
            {row.status.toUpperCase()}
          </span>
        ),
      },
      {
        name: "Action",
        center: true,
        cell: (row) => (
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-light border text-primary"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setShowModel({ show: true, data: row });
              }}
            >
              <i className="bi bi-pencil" />
            </button>
            <button
              className="btn btn-sm btn-light border text-danger"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ),
      },
    ],
    [currentPage, limit],
  );

  return (
    <>
      <section id="main">
        <SearchAndFilter
          componentId="expense-search"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dateRange={dateRange}
          setDateRange={setDateRange}
          title="Expenses"
          searchPlaceholder="Search expense name or paid to"
          addButtonText="Add Expense"
          showAddButton
          onAddClick={() => setShowModel(true)}
          isLoading={loading}
        />

        <div className="card expns-list-card">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={expenses}
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={<Loading />}
              onRowClicked={handleSelectExpense}
              highlightOnHover
              striped
              noDataComponent={
                <div className="text-center p-5">
                  <i className="bi bi-inbox display-1 text-muted"></i>
                  <p className="text-muted px-4 pt-4">No expenses found</p>
                </div>
              }
            />
          </div>
        </div>
        {expenses.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={limit}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
          />
        )}
      </section>

      {showModel && (
        <AddExpense
          onClose={() => setShowModel(false)}
          fetchExpenses={fetchExpenses}
          editData={showModel.data}
        />
      )}

      {selectedExpense && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
            style={{ zIndex: 1049 }}
            onClick={() => setSelectedExpense(null)}
          />
          <PartyCardDetail
            ledgerEntry={selectedExpense}
            onClose={() => setSelectedExpense(null)}
          />
        </>
      )}
    </>
  );
}

export default headerLayoutWrapper(Expenses);
